/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    completeWorkOrderTask,
    createWorkOrderTask,
    createWorkOrderTaskMessage,
    deleteWorkOrderTask,
    deleteWorkOrderTaskMessage,
    editWorkOrderState,
    editWorkOrderTask,
    editWorkOrderTaskMessage,
    getWorkOrderDetails,
    incompleteWorkOrderTask,
} from "./workOrderFunctions";
import { Effect, WorkOrderStateAction, WorkOrderTaskEdit, WorkOrderTaskMessageEdit } from "../../utils/Interfaces";

export function useWorkOrderDetails(workOrderId: number) {
    return useQuery({
        queryKey: ["OFFICE", "WORK_ORDERS", workOrderId],
        queryFn: () => getWorkOrderDetails(workOrderId),
        select: ({ data }) => data,
        enabled: workOrderId !== 0,
    });
}

export function useEditWorkOrderState(workOrderId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (action: WorkOrderStateAction) => {
            // TODO Reload the work order data.
            return editWorkOrderState(workOrderId, action);
        },
        onSuccess: () => {},
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS", workOrderId] }),
    });
}

export function useCreateWorkOrderTask(workOrderId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ label, details }: WorkOrderTaskEdit) => {
            return createWorkOrderTask(workOrderId, label, details);
        },
        onSuccess: () => {},
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS", workOrderId] }),
    });
}

export function useEditWorkOrderTask(workOrderId: number, workOrderTaskId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ label, details }: WorkOrderTaskEdit) => {
            return editWorkOrderTask(workOrderTaskId, label, details);
        },
        onSuccess: () => {},
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS"] }),
    });
}

export function useDeleteWorkOrderTask(workOrderId: number, workOrderTaskId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => {
            return deleteWorkOrderTask(workOrderTaskId);
        },
        onSuccess: () => {},
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS"] }),
    });
}

export function useCompleteWorkOrderTask(workOrderId: number, workOrderTaskId: number, action: Effect = () => {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => {
            return completeWorkOrderTask(workOrderTaskId);
        },
        onSuccess: () => {},
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS"] });
            action();
        },
    });
}

export function useIncompleteWorkOrderTask(workOrderId: number, workOrderTaskId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => {
            return incompleteWorkOrderTask(workOrderTaskId);
        },
        onSuccess: () => {},
        onSettled: () => queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS"] }),
    });
}

export function useCreateWorkOrderTaskMessage(workOrderId: number, workOrderTaskId: number, action: Effect = () => {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ message }: WorkOrderTaskMessageEdit) => {
            // TODO Reload the work order data.
            return createWorkOrderTaskMessage(workOrderTaskId, message);
        },
        onSuccess: () => {},
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS"] });
            action();
        },
    });
}

export function useEditWorkOrderTaskMessage(workOrderId: number, workOrderTaskMessageId: number, action: Effect = () => {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ message }: WorkOrderTaskMessageEdit) => {
            // TODO Reload the work order data.
            return editWorkOrderTaskMessage(workOrderTaskMessageId, message);
        },
        onSuccess: () => {},
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS"] });
            action();
        },
    });
}

export function useDeleteWorkOrderTaskMessage(workOrderId: number, workOrderTaskMessageId: number, action: Effect = () => {}) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ message }: WorkOrderTaskMessageEdit) => {
            // TODO Reload the work order data.
            return deleteWorkOrderTaskMessage(workOrderTaskMessageId);
        },
        onSuccess: () => {},
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS"] });
            action();
        },
    });
}
